import { cloneElement, createContext, isValidElement, useCallback, useEffect, useId, useContext } from "react";
import { createPortal } from "react-dom";
import { useModalExit } from "./useModalExit";
import { ModalBusyProvider, ModalBusyOverlay, useModalBusy } from "./modalBusy";
import "../../style/scss/Dialog.scss";

export const ModalRequestCloseContext = createContext(null);

/** Animated close when inside CmModal; otherwise null. */
export function useModalRequestClose() {
  return useContext(ModalRequestCloseContext);
}

function GuardedRequestClose({ requestClose, children }) {
  const { busy } = useModalBusy();
  const guarded = useCallback(() => {
    if (busy) return;
    requestClose();
  }, [busy, requestClose]);
  return (
    <ModalRequestCloseContext.Provider value={guarded}>
      {children}
    </ModalRequestCloseContext.Provider>
  );
}

function CmModalChrome({
  title,
  children,
  footer,
  size,
  bare,
  testId,
  className,
  titleId,
  hasFooter,
  requestClose,
  exiting,
}) {
  const { busy } = useModalBusy();
  const safeClose = () => {
    if (busy) return;
    requestClose();
  };

  useEffect(() => {
    if (exiting) return undefined;
    function onKeyDown(e) {
      if (e.key === "Escape") safeClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [exiting, busy, requestClose]);

  const sizeClass =
    size === "form"
      ? "cm-modal-dialog--form"
      : size === "xl"
        ? "cm-modal-dialog--xl"
        : size === "wide"
          ? "cm-modal-dialog--wide"
          : "";

  const overlayClass = ["cm-modal-overlay", exiting ? "is-exiting" : "is-entering"]
    .filter(Boolean)
    .join(" ");

  const body = isValidElement(children)
    ? cloneElement(children, { onClose: requestClose })
    : children;

  const footerNode = isValidElement(footer)
    ? cloneElement(footer, { onClick: () => safeClose() })
    : typeof footer === "function"
      ? footer(safeClose)
      : footer;

  return (
    <div
      className={overlayClass}
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-busy={busy}
      aria-labelledby={title ? titleId : undefined}
    >
      <button
        type="button"
        className="cm-modal-backdrop"
        aria-label="Close"
        onClick={safeClose}
        disabled={busy}
      />
      <div className={`cm-modal-dialog ${sizeClass} ${className} ${busy ? "is-busy" : ""}`.trim()}>
        {title ? (
          <div className="cm-modal-dialog__header">
            <h2 id={titleId}>{title}</h2>
            <button
              type="button"
              className="cm-modal-dialog__close"
              aria-label="Close"
              onClick={safeClose}
              disabled={busy}
            >
              ×
            </button>
          </div>
        ) : null}

        <div className="cm-modal-dialog__body">
          {bare ? (
            body
          ) : (
            <div className="cm-modal-embedded">
              <div className="cm-modal-body-pad">{body}</div>
            </div>
          )}
        </div>

        {hasFooter ? (
          <div className="cm-modal-dialog__footer form-actions">{footerNode}</div>
        ) : null}

        <ModalBusyOverlay />
      </div>
    </div>
  );
}

/**
 * Shared app modal chrome (login-style): fixed header/footer, scrollable body.
 *
 * @param {boolean} bare - when true, children fill the body (e.g. a form that owns its own footer band).
 *   Prefer passing `footer` so actions stay pinned; bare forms use sticky CSS as a fallback.
 */
export default function CmModal({
  open = true,
  onClose,
  title,
  children,
  footer,
  size = "default",
  bare = false,
  testId,
  className = "",
}) {
  const titleId = useId();
  const hasFooter = footer != null && footer !== false;
  const { rendered, exiting, requestClose } = useModalExit({ open, onClose });

  if (!rendered) return null;

  return createPortal(
    <ModalBusyProvider>
      <GuardedRequestClose requestClose={requestClose}>
        <CmModalChrome
          title={title}
          children={children}
          footer={footer}
          size={size}
          bare={bare}
          testId={testId}
          className={className}
          titleId={titleId}
          hasFooter={hasFooter}
          requestClose={requestClose}
          exiting={exiting}
        />
      </GuardedRequestClose>
    </ModalBusyProvider>,
    document.body
  );
}
